import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { FormBuilder, FormGroup, Validators, ValidationErrors, ValidatorFn, AbstractControl, FormControl } from '@angular/forms'
import { Subject, merge, Observable } from 'rxjs'
import { finalize, takeUntil, shareReplay, switchMap, tap, filter } from 'rxjs/operators'
import { Router } from '@angular/router'

import { User } from '@shared/models/user.model'
import { ClientsService } from '@app/modules/admin/clients/clients.service'
import { Client } from '@shared/models/client.model'
import { RoleService } from '@shared/services/role.service'
import { Role } from '@shared/models/role.model'
import { AuthService } from '@app/modules/auth/auth.service'
import { takeFormSnapshot } from '@core/helpers/take-form-snapshot'
import { RoleType } from '@core/types/role.type'
import { handleFormErrors } from '@core/helpers/handle-form-errors'
import { TrackedUnsavedForm, UnsavedFormTracker } from '@core/services/unsaved-form-tracker.service'
import { UtilityService } from '@core/services/utility.service'
import { DriverRouteScopeLabel } from '@shared/models/driver.model'
import { Timezone } from '@app/shared/models/timezone.model'
import { DataService } from '@app/shared/services/data.service'

@Component({
  selector: 'lin-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
  standalone: false,
})
export class UserFormComponent implements OnInit, OnDestroy, TrackedUnsavedForm {
  form: FormGroup
  isSubmitting = false
  formError: string
  roles: Role[]
  timezones: Timezone[]
  isFormChanged: ReturnType<typeof takeFormSnapshot>
  DriverRouteScopeLabel = DriverRouteScopeLabel

  private user: User
  private defaultRoleId: string
  private onDestroy$ = new Subject<void>()

  get isCreateDriver(): boolean {
    return this.route.url.includes('driver')
  }

  get canBeDualAccount(): boolean {
    const roleId = +this.form?.get('role')?.value
    return roleId === 6 || roleId === 7
  }

  get hasDriverFields(): boolean {
    return this.form?.get('role')?.value === 8 || (this.canBeDualAccount && <boolean>this.form?.get('isDualAccount')?.value)
  }

  @Input() client: Client
  @Output() save = new EventEmitter<unknown>()

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly clientsService: ClientsService,
    private readonly authService: AuthService,
    private readonly roleService: RoleService,
    private readonly unsavedFormTrackerService: UnsavedFormTracker,
    private readonly utilityService: UtilityService,
    private readonly dataService: DataService,
    private readonly route: Router,
  ) {}

  ngOnInit(): void {
    this.initRoles()
    this.unsavedFormTrackerService.track(this)
  }

  initRoles(): void {
    const byRoleType =
      (roleType: RoleType) =>
      ({ name }: Role) =>
        name === roleType
    const findRoleIdByType = (role: RoleType) => this.roles.find(byRoleType(role))?.id ?? this.roles[0]?.id

    const roles$: Observable<Role[]> = this.authService.currentUser$.pipe(
      tap((user) => (this.user = user)),
      switchMap(() => this.roleService.clientTeamRoles$),
      tap((roles) => (this.roles = roles)),
      shareReplay(),
    )

    const admin$: Observable<Role[]> = roles$.pipe(
      filter(() => this.user.role.name !== RoleType.Manager),
      tap(() => (this.defaultRoleId = findRoleIdByType(RoleType.Admin))),
    )

    const manager$: Observable<Role[]> = roles$.pipe(
      filter(() => this.user.role.name === RoleType.Manager),
      tap(() => {
        this.roles = this.roles.filter(byRoleType(RoleType.Driver))
        this.defaultRoleId = findRoleIdByType(RoleType.Driver)
      }),
    )

    merge(admin$, manager$)
      .pipe(
        switchMap(() => this.dataService.getTimezones()),
        takeUntil(this.onDestroy$),
      )
      .subscribe((timezones) => {
        if (this.isCreateDriver) this.defaultRoleId = findRoleIdByType(RoleType.Driver)
        this.timezones = timezones

        this.initForm()
      })
  }

  patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    /* eslint-disable */
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null
      }

      return regex.test(control.value) ? null : error
    }
    /* eslint-enable */
  }

  initForm(): void {
    this.form = this.formBuilder.group({
      name: ['', Validators.required],
      surname: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      role: [this.defaultRoleId, Validators.required],
      isDualAccount: false,
      driverId: '',
      driverSensorId: '',
      driverRouteScope: '',
      position: '',
      timezone: this.client?.timezone || '',
      phone: ['', [Validators.required, Validators.minLength(8), this.patternValidator(/^\+(?:[\d]*)$/, { phoneNumber: true })]],
    })

    this.initListenerDriverIdValueChanges()
    this.isFormChanged = takeFormSnapshot(this.form)
  }

  private initListenerDriverIdValueChanges(): void {
    this.form.controls.driverId.valueChanges
      .pipe(
        filter(Boolean),
        takeUntil(this.onDestroy$),
        switchMap((driverId: string) => this.clientsService.checkDriverId(this.client.team.id, this.user.id, driverId)),
      )
      .subscribe((isUniqueDriverId) => {
        const error = isUniqueDriverId ? null : { existingName: true }

        this.form.controls.driverId.setErrors(error)
      })
  }

  saveData(): void {
    this.isSubmitting = true
    const saveData = this.prepareDataForSave()

    this.clientsService
      .addClientUser(this.client.id, saveData)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe((user) => {
        this.isFormChanged = takeFormSnapshot(this.form)
        this.save.emit(user)
      }, handleFormErrors(this.form))
  }

  prepareDataForSave(): Partial<User> {
    /* eslint-disable */
    const formData = this.form.value

    let data = {
      name: formData.name,
      surname: formData.surname,
      email: formData.email,
      roleId: formData.role,
      isDualAccount: this.canBeDualAccount && formData.isDualAccount,
      driverId: this.hasDriverFields ? formData.driverId : null,
      driverSensorId: this.hasDriverFields ? this.utilityService.macAddressToString(formData.driverSensorId) : null,
      driverRouteScope: formData.driverRouteScope,
      position: formData.position,
      phone: formData.phone,
      timezone: formData.timezone,
    }

    return data
    /* eslint-enable */
  }

  ngOnDestroy(): void {
    this.unsavedFormTrackerService.discard(this)
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
